import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DollarSign, TrendingUp, Clock, BarChart3, RefreshCw } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ROIDashboardData, FinancialEventType } from '@/types/bi';
import biApi from '@/services/biApi';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { toast } from '@/components/ui/use-toast';

interface ROIDashboardProps {
  clientId?: number;
  startDate?: string;
  endDate?: string;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

const ROIDashboard: React.FC<ROIDashboardProps> = ({ clientId, startDate, endDate }) => {
  const [data, setData] = useState<ROIDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadData = async () => {
    if (!clientId) {
      console.warn('⚠️ [ROIDashboard] loadData chamado sem clientId');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('🔍 [ROIDashboard] Carregando dados de ROI:', {
        clientId,
        startDate,
        endDate,
      });

      const roiData = await biApi.getRoiDashboard(clientId, startDate, endDate);
      
      console.log('✅ [ROIDashboard] Dados recebidos:', {
        hasData: !!roiData,
        dataType: typeof roiData,
        dataKeys: roiData ? Object.keys(roiData) : [],
        preview: roiData ? JSON.stringify(roiData).substring(0, 200) : 'null',
      });

      if (!roiData) {
        console.warn('⚠️ [ROIDashboard] Dados recebidos são null ou undefined');
        setData(null);
        return;
      }

      // Validar estrutura básica dos dados
      if (!roiData.period || !roiData.byEventType) {
        console.error('❌ [ROIDashboard] Estrutura de dados inválida:', roiData);
        throw new Error('Estrutura de dados inválida recebida da API');
      }

      setData(roiData);
    } catch (error: any) {
      console.error('❌ [ROIDashboard] Erro ao carregar dados de ROI:', error);
      console.error('❌ [ROIDashboard] Tipo do erro:', typeof error);
      console.error('❌ [ROIDashboard] Mensagem:', error?.message);
      console.error('❌ [ROIDashboard] Stack:', error?.stack);
      console.error('❌ [ROIDashboard] Erro completo:', JSON.stringify(error, null, 2));
      
      const errorMessage = error?.message || error?.error || 'Não foi possível carregar os dados de ROI';
      
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
      setData(null);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    console.log('🔄 [ROIDashboard] useEffect executado:', {
      clientId,
      startDate,
      endDate,
      clientIdDefined: clientId !== undefined,
    });

    if (clientId !== undefined) {
      loadData();
    } else {
      console.log('⚠️ [ROIDashboard] clientId não definido, limpando dados');
      setIsLoading(false);
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, startDate, endDate]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
  };

  if (!clientId) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Selecione um cliente para visualizar o dashboard de ROI</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return <AtomicLoadingScreen />;
  }

  if (!data) {
    console.log('⚠️ [ROIDashboard] Renderizando sem dados');
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-gray-500">Nenhum dado disponível</p>
        </CardContent>
      </Card>
    );
  }

  // Validação adicional antes de renderizar
  if (!data.period || !data.byEventType) {
    console.error('❌ [ROIDashboard] Dados inválidos para renderização:', data);
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-red-500">Erro: Estrutura de dados inválida</p>
        </CardContent>
      </Card>
    );
  }

  // Normalizar valores para evitar undefined/null
  const normalizedData = {
    ...data,
    totalRecovered: data.totalRecovered ?? 0,
    totalGenerated: data.totalGenerated ?? 0,
    conversionRate: data.conversionRate ?? 0,
    averagePaymentTimePix: data.averagePaymentTimePix ?? null,
    byEventType: (data.byEventType || []).map(item => ({
      ...item,
      totalRecovered: item.totalRecovered ?? 0,
      totalGenerated: item.totalGenerated ?? 0,
      conversionRate: item.conversionRate ?? 0,
      count: item.count ?? 0,
    })),
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(value)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatPercentage = (value: number | null | undefined, decimals: number = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '0.00';
    return value.toFixed(decimals);
  };

  const formatHours = (hours: number | null) => {
    if (hours === null) return 'N/A';
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return `${days}d ${remainingHours}h`;
  };

  const pieData = data.byEventType
    .filter(item => item && item.totalRecovered !== null && item.totalRecovered !== undefined)
    .map((item) => ({
      name: item.event_type?.replace('_', ' ') || 'Desconhecido',
      value: item.totalRecovered || 0,
    }));

  const barData = data.byEventType
    .filter(item => item)
    .map((item) => ({
      name: item.event_type?.replace('_', ' ') || 'Desconhecido',
      recuperado: item.totalRecovered || 0,
      emitido: item.totalGenerated || 0,
    }));

  try {
    console.log('🎨 [ROIDashboard] Renderizando componente com dados:', {
      hasData: !!data,
      hasPeriod: !!data?.period,
      hasByEventType: Array.isArray(data?.byEventType),
      byEventTypeLength: data?.byEventType?.length || 0,
    });

    return (
      <div className="space-y-6">
        {/* Header com refresh */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Dashboard de ROI</h2>
            <p className="text-sm text-gray-500">
              Período: {format(new Date(normalizedData.period.startDate), 'dd/MM/yyyy', { locale: ptBR })} até{' '}
              {format(new Date(normalizedData.period.endDate), 'dd/MM/yyyy', { locale: ptBR })}
            </p>
          </div>
        <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Cards de métricas principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Recuperado</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(normalizedData.totalRecovered)}</div>
            <p className="text-xs text-gray-500 mt-1">Valor pago no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Emitido</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(normalizedData.totalGenerated)}</div>
            <p className="text-xs text-gray-500 mt-1">Valor gerado no período</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatPercentage(normalizedData.conversionRate)}%
            </div>
            <p className="text-xs text-gray-500 mt-1">Recuperado / Emitido</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio PIX</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatHours(normalizedData.averagePaymentTimePix)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Tempo médio de recebimento</p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Pizza - Distribuição por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Tipo de Evento</CardTitle>
            <CardDescription>Valor recuperado por tipo</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{}}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Barras - Comparação Recuperado vs Emitido */}
        <Card>
          <CardHeader>
            <CardTitle>Recuperado vs Emitido por Tipo</CardTitle>
            <CardDescription>Comparação de valores</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{}}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="recuperado" fill="#10b981" name="Recuperado" />
                  <Bar dataKey="emitido" fill="#3b82f6" name="Emitido" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tabela detalhada */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Tipo de Evento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Tipo</th>
                  <th className="text-right p-2">Recuperado</th>
                  <th className="text-right p-2">Emitido</th>
                  <th className="text-right p-2">Taxa</th>
                  <th className="text-right p-2">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {normalizedData.byEventType.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <Badge variant="outline">{item.event_type.replace('_', ' ')}</Badge>
                    </td>
                    <td className="text-right p-2 font-medium text-green-600">
                      {formatCurrency(item.totalRecovered)}
                    </td>
                    <td className="text-right p-2">{formatCurrency(item.totalGenerated)}</td>
                    <td className="text-right p-2">
                      <Badge variant={(item.conversionRate ?? 0) >= 50 ? 'default' : 'secondary'}>
                        {formatPercentage(item.conversionRate)}%
                      </Badge>
                    </td>
                    <td className="text-right p-2">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
    );
  } catch (renderError: any) {
    console.error('❌ [ROIDashboard] Erro durante renderização:', renderError);
    console.error('❌ [ROIDashboard] Stack:', renderError?.stack);
    
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-red-500">
            Erro ao renderizar dashboard: {renderError?.message || 'Erro desconhecido'}
          </p>
          <p className="text-center text-sm text-gray-500 mt-2">
            Verifique o console para mais detalhes
          </p>
        </CardContent>
      </Card>
    );
  }
};

export default ROIDashboard;

